/**
 * Lista de reservas — a mesa de trabalho da operação (Fase 4).
 *
 * O filtro padrão é "só as vivas": a pergunta do dia a dia é "o que está em
 * curso", e as finalizadas/canceladas são consulta de histórico, não trabalho.
 * O status é sempre selo com rótulo (regra do CLAUDE.md), e a linha inteira
 * leva ao detalhe — onde moram a timeline e todas as ações.
 */

import { ROTULO_DO_STATUS_DE_RESERVA, STATUS_DE_RESERVA } from '@locaveri/shared'
import type { ColumnDef } from '@tanstack/react-table'
import { Link } from '@tanstack/react-router'
import { CalendarRange, Plus } from 'lucide-react'
import { type ReactElement, useState } from 'react'

import { Paginacao } from '@/components/data/paginacao'
import { TabelaDeDados } from '@/components/data/tabela-de-dados'
import { EmptyState } from '@/components/state/empty-state'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { BadgeDeStatusDeReserva } from '@/features/reservas/badges'
import { useReservas, type ReservaResumo } from '@/features/reservas/api'
import { formatarDataHora } from '@/lib/datas'
import { formatarMoedaDoWire } from '@/lib/dinheiro'
import { temPermissao } from '@/lib/permissoes'

const COLUNAS: readonly ColumnDef<ReservaResumo>[] = [
  {
    id: 'numero',
    header: 'Nº',
    cell: ({ row }) => (
      <Link
        to="/reservas/$reservaId"
        params={{ reservaId: row.original.id }}
        className="font-semibold text-foreground underline-offset-4 hover:underline"
      >
        {row.original.numero}
      </Link>
    ),
  },
  {
    id: 'conta',
    header: 'Cliente',
    cell: ({ row }) => row.original.conta.nome,
  },
  {
    id: 'categoria',
    header: 'Categoria',
    cell: ({ row }) => row.original.categoria.nome,
  },
  {
    id: 'periodo',
    header: 'Período',
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-fg-secondary">
        {formatarDataHora(row.original.retirada)} → {formatarDataHora(row.original.devolucao)}
      </span>
    ),
  },
  {
    id: 'veiculo',
    header: 'Veículo',
    cell: ({ row }) =>
      row.original.veiculoAlocado === null ? (
        <span className="text-fg-muted">a alocar</span>
      ) : (
        <span className="whitespace-nowrap">
          {row.original.veiculoAlocado.placa} · {row.original.veiculoAlocado.modelo}
        </span>
      ),
  },
  {
    id: 'total',
    header: 'Total',
    cell: ({ row }) => (
      <span className="tabular-nums">{formatarMoedaDoWire(row.original.total)}</span>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => <BadgeDeStatusDeReserva status={row.original.status} />,
  },
]

export function ReservasPage(): ReactElement {
  const [pagina, setPagina] = useState(1)
  const [status, setStatus] = useState('')
  const [apenasVivas, setApenasVivas] = useState(true)
  const [busca, setBusca] = useState('')

  const consulta = useReservas({
    pagina,
    status: status === '' ? undefined : status,
    apenasVivas: status === '' ? apenasVivas : undefined,
    busca: busca === '' ? undefined : busca,
  })

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Reservas</h1>
          <p className="text-sm text-fg-secondary">
            Toda locação começa aqui — e a máquina de estados garante que ela só anda pelos caminhos
            declarados.
          </p>
        </div>
        {/*
          NOVA RESERVA LEVA AO SIMULADOR, e não a um diálogo próprio.

          Havia aqui um diálogo que cotava de novo, pior: sem local, sem
          opcionais e sem cupom. Ou seja, o atendente que precisasse de qualquer
          um dos três cotava no simulador, anotava, e redigitava aqui — o
          caminho mais curto para "cotei X, reservei Y".

          O simulador já cota com tudo e mostra o breakdown. Fechar a reserva e
          mandar o link para o cliente passaram a ser dois botões DENTRO do
          cartão da categoria cotada, ao lado do total. Uma tela a menos, e a
          reserva nasce da cotação que o cliente ouviu no telefone.
        */}
        {temPermissao('reservation.create') && temPermissao('quote.create') ? (
          <Button asChild>
            <Link to="/simulador">
              <Plus /> Nova reserva
            </Link>
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="w-56">
          <Label htmlFor="filtro-status">Status</Label>
          <Select
            id="filtro-status"
            value={status}
            onChange={(evento) => {
              setStatus(evento.target.value)
              setPagina(1)
            }}
          >
            <option value="">Todos</option>
            {STATUS_DE_RESERVA.map((valor) => (
              <option key={valor} value={valor}>
                {ROTULO_DO_STATUS_DE_RESERVA[valor].rotulo}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-64">
          <Label htmlFor="filtro-busca">Buscar</Label>
          <Input
            id="filtro-busca"
            placeholder="Cliente ou placa…"
            value={busca}
            onChange={(evento) => {
              setBusca(evento.target.value)
              setPagina(1)
            }}
          />
        </div>
        {status === '' ? (
          <label className="flex items-center gap-2 pb-2 text-sm text-fg-secondary">
            <Checkbox
              checked={apenasVivas}
              onChange={(evento) => {
                setApenasVivas(evento.target.checked)
                setPagina(1)
              }}
            />
            Só as em curso
          </label>
        ) : null}
      </div>

      <TabelaDeDados
        colunas={COLUNAS}
        dados={consulta.data?.itens ?? []}
        carregando={consulta.isPending}
        erro={consulta.error ?? undefined}
        aoTentarNovamente={() => {
          void consulta.refetch()
        }}
        legenda="Reservas do período filtrado"
        estadoVazio={
          <EmptyState
            icone={CalendarRange}
            titulo="Nenhuma reserva por aqui"
            descricao="Quando uma cotação virar reserva, ela aparece nesta lista."
          />
        }
      />

      <Paginacao
        pagina={pagina}
        tamanho={consulta.data?.tamanho ?? 20}
        total={consulta.data?.total ?? 0}
        aoMudarDePagina={setPagina}
        desabilitado={consulta.isFetching}
      />
    </div>
  )
}
